import { create } from 'zustand';
import { getAccumulatorBySubscriberNum } from '../services/accumulatorApi';
import type { SessionUser } from '../utils/session';
import type { SubscriberBenefitAmount } from '../types/api';

/** Rendered when an amount is unavailable. Matches the legacy AngularJS placeholder. */
export const DASH = '-';

const LOAD_ERROR = 'We are unable to display your deductible information at this time.';

export interface AccumulatorDisplayValues {
  deductibleYTD: string;
  deductible: string;
  outOfPocketYTD: string;
  maxOutOfPocket: string;
  familyDeductibleYTD: string;
  familyDeductible: string;
  familyOutOfPocketYTD: string;
  familyMaxOutOfPocket: string;
}

function toAmount(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toFixed(2) : DASH;
}

/**
 * Whether this member's accumulators come from an SDC build (DAED-271).
 *
 * The single place in the app that inspects the raw indicator — everything
 * downstream consumes the boolean. Defaults to false for anything that is not
 * literally `true`, so a missing, null, or unexpectedly-typed field can never
 * flip a standard member onto the SDC path.
 *
 * Reads the per-member record rather than the response envelope: Laker declares
 * `IsSdc` on `SubscriberBenefitAmountSoapResponse`, which is what populates
 * `Data[]`, and ext-svc passes that object straight through.
 */
export function isSdcMember(record: SubscriberBenefitAmount | null | undefined): boolean {
  return record?.IsSdc === true;
}

/**
 * Formats accumulator amounts for display.
 *
 * Two distinct modes, because a member's view is wholly one or the other:
 *
 * **SDC (`isSdc === true`)** — every maximum renders as a dash, always, even
 * when the data carries a positive value. SDC plans hold multiple individual
 * and family maximums across pharmacy tiers, so any single displayed maximum
 * would mislead a member whose YTD legitimately exceeds one tier while still
 * accumulating toward another. Year-to-date amounts display on their own,
 * independent of the maximum. Locked decision (DAED-40, Reema Al-Mutair):
 * dash for ALL SDC builds regardless of tier count — do not add a
 * "show the maximum when there is only one" branch.
 *
 * **Standard (`isSdc === false`)** — port of handleAccumulatorData() from
 * www/app/controllers/accumulatorController.js. The maximum gates its own pair:
 * when a maximum is not greater than zero, BOTH it and its year-to-date amount
 * are dashed. That coupling is the mechanism behind the "SDC members see
 * dashes" problem DAED-40 describes; it is preserved here verbatim because it
 * remains correct for standard plans.
 */
export function formatAccumulatorValues(
  data: SubscriberBenefitAmount,
  isSdc = false
): AccumulatorDisplayValues {
  if (isSdc) {
    return {
      deductible: DASH,
      deductibleYTD: toAmount(data.DeductibleYTD),
      maxOutOfPocket: DASH,
      outOfPocketYTD: toAmount(data.OutOfPocketYTD),
      familyDeductible: DASH,
      familyDeductibleYTD: toAmount(data.FamilyDeductibleYTD),
      familyMaxOutOfPocket: DASH,
      familyOutOfPocketYTD: toAmount(data.FamilyOutOfPocketYTD),
    };
  }

  const hasIndividualDeductible = (data.Deductible ?? 0) > 0;
  const hasIndividualOutOfPocket = (data.MaxOutOfPocket ?? 0) > 0;
  const hasFamilyDeductible = (data.FamilyDeductible ?? 0) > 0;
  const hasFamilyOutOfPocket = (data.FamilyMaxOutOfPocket ?? 0) > 0;

  return {
    deductible: hasIndividualDeductible ? toAmount(data.Deductible) : DASH,
    deductibleYTD: hasIndividualDeductible ? toAmount(data.DeductibleYTD) : DASH,
    maxOutOfPocket: hasIndividualOutOfPocket ? toAmount(data.MaxOutOfPocket) : DASH,
    outOfPocketYTD: hasIndividualOutOfPocket ? toAmount(data.OutOfPocketYTD) : DASH,
    familyDeductible: hasFamilyDeductible ? toAmount(data.FamilyDeductible) : DASH,
    familyDeductibleYTD: hasFamilyDeductible ? toAmount(data.FamilyDeductibleYTD) : DASH,
    familyMaxOutOfPocket: hasFamilyOutOfPocket ? toAmount(data.FamilyMaxOutOfPocket) : DASH,
    familyOutOfPocketYTD: hasFamilyOutOfPocket ? toAmount(data.FamilyOutOfPocketYTD) : DASH,
  };
}

export interface AccumulatorStore {
  values: AccumulatorDisplayValues | null;
  /** True when the member is on an SDC plan — drives the info icons. */
  isSdc: boolean;
  loading: boolean;
  error: string | null;

  fetchAccumulator: (user: SessionUser) => Promise<void>;
  reset: () => void;
}

const initialState: Omit<AccumulatorStore, 'fetchAccumulator' | 'reset'> = {
  values: null,
  isSdc: false,
  loading: false,
  error: null,
};

export const useAccumulatorStore = create<AccumulatorStore>(set => ({
  ...initialState,

  async fetchAccumulator(user: SessionUser) {
    set({ loading: true, error: null });

    try {
      const response = await getAccumulatorBySubscriberNum({
        WebAccountLinkToken: user.LinkToken ?? '',
        Subscribernum: user.LakerSubscriberNum ?? '',
        PersonCode: user.LakerPersonCode ?? '',
      });

      const record = response?.StatusCode === 'SUCCESS' ? response.Data?.[0] : undefined;

      if (!record) {
        // Legacy indexed Data[0] unguarded and had no error branch, so a failed or
        // empty response left the spinner running indefinitely.
        set({ loading: false, error: LOAD_ERROR, values: null, isSdc: false });
        return;
      }

      const isSdc = isSdcMember(record);

      set({
        loading: false,
        error: null,
        isSdc,
        values: formatAccumulatorValues(record, isSdc),
      });
    } catch {
      set({ loading: false, error: LOAD_ERROR, values: null, isSdc: false });
    }
  },

  reset() {
    set({ ...initialState });
  },
}));
